/* ==========================================================================
   SIMPATICO PDP  —  sim-pdp.js
   --------------------------------------------------------------------------
   Shared behaviour for every sim-* section. Vanilla JS, no build step, safe to
   load once per page, re-initialises cleanly in the theme editor.

   HOST THEME INTEGRATION
   The one place this file is not theme-agnostic is the cart. Multi's cart
   drawer only opens when something publishes `product-form:submit` (or
   `cart:add`) through the theme's closure-scoped PubSub — a bare
   fetch('/cart/add.js') adds the line item but leaves the drawer shut and its
   contents stale. We therefore publish through a tiny custom element that
   extends the theme's Core class, and fall back to a plain fetch plus the
   documented `cart:refresh` event on any theme that does not expose Global.
   ========================================================================== */
(function () {
  'use strict';

  var BOOTED = 'data-sim-booted';

  /* ------------------------------------------------------------------ util */

  function routeRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /**
   * Format cents using the shop's money_format string. Handles every Shopify
   * amount token plus any surrounding markup, so stores on comma-separator or
   * no-decimal formats render correctly.
   */
  function money(cents, format) {
    var fmt = format || '${{amount}}';
    var value = Math.round(cents);

    function group(n, sep) {
      return n.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    }

    function build(groupSep, decimalSep, places) {
      var neg = value < 0;
      var abs = Math.abs(value);
      var whole = group(Math.floor(abs / 100).toString(), groupSep);
      var cent = ('00' + (abs % 100)).slice(-2);
      return (neg ? '-' : '') + (places === 0 ? whole : whole + decimalSep + cent);
    }

    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, token) {
      switch (token) {
        case 'amount': return build(',', '.', 2);
        case 'amount_no_decimals': return build(',', '.', 0);
        case 'amount_with_comma_separator': return build('.', ',', 2);
        case 'amount_no_decimals_with_comma_separator': return build('.', ',', 0);
        case 'amount_with_apostrophe_separator': return build("'", '.', 2);
        case 'amount_no_decimals_with_space_separator': return build(' ', '.', 0);
        case 'amount_with_space_separator': return build(' ', ',', 2);
        default: return build(',', '.', 2);
      }
    });
  }

  /* ------------------------------------------------ host theme cart bridge */

  var bridgeReady = false;

  /**
   * Returns an element able to publish on the host theme's event bus, or null
   * when the host theme does not expose one.
   */
  function cartBridge() {
    var G = window.Global;
    if (!G || !G.Core || !G.Events || !G.Events.PRODUCT_FORM_SUBMIT) return null;
    try {
      if (!bridgeReady && !window.customElements.get('sim-cart-bridge')) {
        window.customElements.define('sim-cart-bridge', class extends G.Core {
          render() { /* publish-only; subscribes to nothing */ }
        });
      }
      bridgeReady = true;
      return document.querySelector('sim-cart-bridge');
    } catch (e) {
      return null;
    }
  }

  /**
   * Add to cart through the host theme where possible so its drawer opens and
   * its cart sections re-render. Resolves on success, rejects on failure.
   */
  function addToCart(formData, opts) {
    opts = opts || {};
    var bridge = cartBridge();

    if (bridge) {
      // Strip empty values so blank line-item properties are not submitted.
      Array.prototype.slice.call(formData.entries()).forEach(function (pair) {
        if (typeof pair[1] === 'string' && pair[1].trim() === '') formData.delete(pair[0]);
      });
      bridge.pub(window.Global.Events.PRODUCT_FORM_SUBMIT, { formData: formData });
      // cart-provider owns the request from here and surfaces its own errors.
      return Promise.resolve({ viaTheme: true });
    }

    return fetch(routeRoot() + 'cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData
    }).then(function (res) {
      return res.json().then(function (body) {
        if (!res.ok) throw new Error(body.description || body.message || 'Add to cart failed');
        return body;
      });
    }).then(function (body) {
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('sim:cart:added', { bubbles: true, detail: body }));
      if (opts.redirect) window.location.href = routeRoot() + 'cart';
      return body;
    });
  }

  /* ---------------------------------------------------------------- gallery */

  function initGallery(root) {
    var main = root.querySelector('[data-sim-gallery-main]');
    var thumbs = root.querySelectorAll('[data-sim-thumb]');
    if (!main || !thumbs.length) return;

    function activate(thumb) {
      var src = thumb.getAttribute('data-src');
      var alt = thumb.getAttribute('data-alt') || '';
      var img = main.querySelector('img');
      if (img && src) {
        img.setAttribute('src', src);
        img.setAttribute('alt', alt);
        img.removeAttribute('srcset');
      }
      thumbs.forEach(function (x) { x.setAttribute('aria-current', 'false'); });
      thumb.setAttribute('aria-current', 'true');
    }

    thumbs.forEach(function (t) {
      t.addEventListener('click', function () { activate(t); });
    });

    // Keep the thumbnail strip in sync when a variant swaps the main image.
    root.addEventListener('sim:gallery:sync', function (e) {
      var src = e.detail && e.detail.src;
      if (!src) return;
      thumbs.forEach(function (t) {
        t.setAttribute('aria-current', t.getAttribute('data-src') === src ? 'true' : 'false');
      });
    });
  }

  /* ------------------------------------------------------------ variant UI */

  function initVariants(root) {
    var form = root.querySelector('[data-sim-product-form]');
    var dataEl = root.querySelector('[data-sim-variants]');
    if (!form || !dataEl) return;

    var variants;
    try { variants = JSON.parse(dataEl.textContent || '[]'); } catch (e) { return; }
    if (!variants.length) return;

    var idInput = form.querySelector('[data-sim-variant-id]');
    var priceEl = root.querySelector('[data-sim-price]');
    var compareEl = root.querySelector('[data-sim-compare-price]');
    var savingEl = root.querySelector('[data-sim-saving]');
    var stockEl = root.querySelector('[data-sim-stock]');
    var stockRow = stockEl ? stockEl.closest('[data-sim-stock-row]') : null;
    var skuEl = root.querySelector('[data-sim-sku]');
    var submit = form.querySelector('[data-sim-submit]');
    var format = root.getAttribute('data-money-format') || '${{amount}}';
    var mainImg = root.querySelector('[data-sim-gallery-main] img');
    var groups = Array.prototype.slice.call(root.querySelectorAll('[data-sim-option-group]'));

    function currentSelection() {
      return groups.map(function (group) {
        var checked = group.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }

    function matchVariant(picked) {
      return variants.find(function (v) {
        return picked.every(function (val, i) { return val === null || v.options[i] === val; });
      }) || null;
    }

    /**
     * Grey out option values that cannot be reached from the current
     * selection. Shopify's `value.available` is only accurate on page load;
     * without this, multi-option products let shoppers pick dead combinations.
     */
    function refreshAvailability(picked) {
      groups.forEach(function (group, gi) {
        group.querySelectorAll('input[type="radio"]').forEach(function (input) {
          var probe = picked.slice();
          probe[gi] = input.value;
          var reachable = variants.some(function (v) {
            return v.available && probe.every(function (val, i) { return val === null || v.options[i] === val; });
          });
          // Never disable the value the shopper currently has selected.
          input.disabled = !reachable && !input.checked;
          var label = input.nextElementSibling;
          if (label && label.tagName === 'LABEL') {
            label.setAttribute('data-unavailable', reachable ? 'false' : 'true');
          }
        });
      });
    }

    function paint(variant) {
      var txt = submit ? submit.querySelector('[data-sim-submit-text]') : null;

      if (!variant) {
        if (submit) {
          submit.setAttribute('disabled', 'disabled');
          if (txt) txt.textContent = submit.getAttribute('data-unavailable-label') || 'Unavailable';
        }
        return;
      }

      if (idInput) idInput.value = variant.id;
      if (priceEl) priceEl.textContent = money(variant.price, format);

      if (compareEl) {
        var onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
        compareEl.textContent = onSale ? money(variant.compare_at_price, format) : '';
        compareEl.hidden = !onSale;
        if (savingEl) {
          savingEl.textContent = onSale ? 'Save ' + money(variant.compare_at_price - variant.price, format) : '';
          savingEl.hidden = !onSale;
        }
      }

      if (skuEl) skuEl.textContent = variant.sku || '';

      if (stockEl) {
        var label = stockEl.getAttribute('data-in-stock') || 'In stock';
        if (!variant.available) {
          label = stockEl.getAttribute('data-out-stock') || 'Sold out';
        } else if (variant.inventory_management && variant.inventory_quantity > 0 && variant.inventory_quantity <= 5) {
          label = (stockEl.getAttribute('data-low-stock') || 'Low stock') + ' — ' + variant.inventory_quantity + ' left';
        }
        stockEl.textContent = label;
        if (stockRow) stockRow.setAttribute('data-state', variant.available ? 'in' : 'out');
      }

      if (submit) {
        submit.toggleAttribute('disabled', !variant.available);
        if (txt) {
          txt.textContent = variant.available
            ? (submit.getAttribute('data-label') || 'Add to cart')
            : (submit.getAttribute('data-sold-label') || 'Sold out');
        }
      }

      if (mainImg && variant.featured_image && variant.featured_image.src) {
        mainImg.setAttribute('src', variant.featured_image.src);
        mainImg.removeAttribute('srcset');
        root.dispatchEvent(new CustomEvent('sim:gallery:sync', { detail: { src: variant.featured_image.src } }));
      }

      root.querySelectorAll('[data-sim-sticky-price]').forEach(function (el) {
        el.textContent = money(variant.price, format);
      });

      // Keep the official Shop Pay Installments banner on the right variant.
      root.querySelectorAll('shopify-payment-terms').forEach(function (el) {
        el.setAttribute('variant-id', variant.id);
      });
      if (window.Shopify && window.Shopify.PaymentButton && typeof window.Shopify.PaymentButton.init === 'function') {
        try { window.Shopify.PaymentButton.init(); } catch (e) { /* non-fatal */ }
      }

      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      } catch (e) { /* non-fatal */ }

      document.dispatchEvent(new CustomEvent('sim:variant:change', {
        bubbles: true,
        detail: { variant: variant }
      }));
    }

    function onChange() {
      var picked = currentSelection();
      refreshAvailability(picked);
      paint(matchVariant(picked));
    }

    groups.forEach(function (group) {
      group.querySelectorAll('input').forEach(function (input) {
        input.addEventListener('change', onChange);
      });
    });

    refreshAvailability(currentSelection());

    // Quantity stepper
    var qtyWrap = root.querySelector('[data-sim-qty]');
    if (qtyWrap) {
      var field = qtyWrap.querySelector('input');
      qtyWrap.querySelectorAll('button').forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          var step = parseInt(btn.getAttribute('data-step'), 10) || 1;
          field.value = Math.max(1, (parseInt(field.value, 10) || 1) + step);
          field.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    }
  }

  /* ------------------------------------------------------------ add to cart */

  function initCart(root) {
    root.querySelectorAll('[data-sim-product-form]').forEach(function (form) {
      if (form.getAttribute('data-sim-ajax') !== 'true') return;

      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var submit = form.querySelector('[data-sim-submit]');
        var txt = submit ? submit.querySelector('[data-sim-submit-text]') : null;
        var original = txt ? txt.textContent : '';

        if (txt && submit) txt.textContent = submit.getAttribute('data-adding-label') || 'Adding…';
        if (submit) submit.setAttribute('disabled', 'disabled');

        function restore(label) {
          if (txt) txt.textContent = label;
          if (submit) submit.removeAttribute('disabled');
        }

        addToCart(new FormData(form), { redirect: form.getAttribute('data-sim-redirect') === 'true' })
          .then(function () {
            if (txt && submit) txt.textContent = submit.getAttribute('data-added-label') || 'Added ✓';
            setTimeout(function () { restore(original); }, 1600);
          })
          .catch(function (err) {
            restore((submit && submit.getAttribute('data-error-label')) || 'Try again');
            document.dispatchEvent(new CustomEvent('sim:cart:error', { bubbles: true, detail: { error: err } }));
          });
      });
    });
  }

  /* ------------------------------------------------------------- accordions */

  function initAccordions(root) {
    root.querySelectorAll('[data-sim-accordion]').forEach(function (wrap) {
      var single = wrap.getAttribute('data-single') === 'true';
      wrap.querySelectorAll('[data-sim-accordion-trigger]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var item = btn.closest('[data-sim-accordion-item]');
          if (!item) return;
          var open = item.getAttribute('data-open') === 'true';
          if (single && !open) {
            wrap.querySelectorAll('[data-sim-accordion-item]').forEach(function (i) {
              i.setAttribute('data-open', 'false');
              var trigger = i.querySelector('[data-sim-accordion-trigger]');
              if (trigger) trigger.setAttribute('aria-expanded', 'false');
            });
          }
          item.setAttribute('data-open', open ? 'false' : 'true');
          btn.setAttribute('aria-expanded', open ? 'false' : 'true');
        });
      });
    });
  }

  /* ------------------------------------------------------- sticky ATC bar */

  function initSticky(root) {
    var bar = root.querySelector('[data-sim-sticky-bar]');
    if (!bar) return;
    var anchor = root.querySelector('[data-sim-sticky-anchor]') || root.querySelector('[data-sim-product-form]');
    if (!anchor) return;

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          bar.setAttribute('data-visible', entry.isIntersecting ? 'false' : 'true');
        });
      }, { rootMargin: '0px 0px -100% 0px', threshold: 0 });
      observer.observe(anchor);
      root._simStickyObserver = observer;
    } else {
      var onScroll = function () {
        bar.setAttribute('data-visible', anchor.getBoundingClientRect().bottom < 0 ? 'true' : 'false');
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }

    bar.querySelectorAll('[data-sim-sticky-atc]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var form = root.querySelector('[data-sim-product-form]');
        if (!form) return;
        if (form.requestSubmit) form.requestSubmit();
        else form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      });
    });
  }

  /* ------------------------------------------ dispatch / free shipping line */

  function initShipping(root) {
    var line = root.querySelector('[data-sim-ship][data-sim-ship-threshold]');
    if (!line) return;
    var threshold = parseInt(line.getAttribute('data-sim-ship-threshold'), 10);
    var ok = line.querySelector('.sim-ship__ok');
    var free = line.querySelector('.sim-ship__free');
    var thresh = line.querySelector('.sim-ship__thresh');

    document.addEventListener('sim:variant:change', function (e) {
      var v = e.detail && e.detail.variant;
      if (!v) return;
      line.hidden = !v.available;
      if (ok) ok.hidden = !v.available;
      var isFree = v.price >= threshold;
      if (free) free.hidden = !isFree;
      if (thresh) thresh.hidden = isFree;
    });
  }

  /* --------------------------------------------------------- stock notifier */

  function initNotifier(root) {
    var notify = root.querySelector('[data-sim-notify]');

    document.addEventListener('sim:variant:change', function (e) {
      var v = e.detail && e.detail.variant;
      if (!v) return;
      var title = (v.options || []).join(' / ');

      if (notify) {
        notify.hidden = v.available;
        var label = notify.querySelector('[data-sim-notify-variant-label]');
        var urlField = notify.querySelector('[data-sim-notify-url]');
        var img = notify.querySelector('[data-sim-notify-img]');
        if (label) label.textContent = title;
        if (urlField) {
          try {
            var u = new URL(window.location.href);
            u.searchParams.set('variant', v.id);
            urlField.value = u.toString();
          } catch (err) { /* non-fatal */ }
        }
        if (img && v.featured_image && v.featured_image.src) img.setAttribute('src', v.featured_image.src);
      }

      // Every contact form on the page that tracks the variant — the notifier
      // and the question / pricing-request forms — stays in sync.
      document.querySelectorAll('[data-sim-notify-variant]').forEach(function (el) {
        el.value = title;
      });
    });
  }

  /* -------------------------------------------------- compatibility checker */

  function initCompat(root) {
    var tool = root.querySelector('[data-sim-compat]');
    if (!tool) return;
    var dataEl = tool.querySelector('[data-sim-compat-data]');
    if (!dataEl) return;

    var data;
    try { data = JSON.parse(dataEl.textContent || '{}'); } catch (e) { return; }
    if (!data.copy) return;

    data.bases = data.bases || [];
    data.product = data.product || {};
    data.product.platforms = data.product.platforms || [];

    var state = { platform: null, base: null };
    var result = tool.querySelector('[data-sim-compat-result]');
    if (!result) return;

    function eq(a, b) {
      return String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
    }

    function render() {
      var status = 'idle';
      var title = data.copy.idle_title;
      var body = data.copy.idle_body;
      var fix = '';
      var fixUrl = '';

      if (state.platform && state.base) {
        var base = data.bases.find(function (b) { return b.id === state.base; });
        var productPlatforms = data.product.platforms;

        var platformOk = productPlatforms.length === 0 ||
          productPlatforms.some(function (p) { return eq(p, state.platform); });
        var basePlatformOk = !base || !base.platforms || !base.platforms.length ||
          base.platforms.some(function (p) { return eq(p, state.platform); });
        var qrOk = !base || !base.qr || !data.product.qr || eq(base.qr, data.product.qr);

        if (!platformOk) {
          status = 'no';
          title = (data.copy.platform_title || '').replace('[platform]', state.platform);
          body = (data.copy.platform_body || '').replace('[platform]', state.platform);
          fix = data.copy.platform_fix;
          fixUrl = data.copy.platform_fix_url;
        } else if (!basePlatformOk) {
          status = 'warn';
          title = (data.copy.base_platform_title || '').replace('[base]', base.name).replace('[platform]', state.platform);
          body = (data.copy.base_platform_body || '').replace('[base]', base.name).replace('[platform]', state.platform);
        } else if (!qrOk) {
          status = 'warn';
          title = (data.copy.qr_title || '').replace('[base_qr]', base.qr).replace('[product_qr]', data.product.qr);
          body = (data.copy.qr_body || '').replace('[base_qr]', base.qr).replace('[product_qr]', data.product.qr).replace('[base]', base.name);
          fix = base.adapter_label || data.copy.qr_fix;
          fixUrl = base.adapter_url || data.copy.qr_fix_url;
        } else {
          status = 'yes';
          title = (data.copy.ok_title || '').replace('[base]', base.name);
          body = (data.copy.ok_body || '').replace('[base]', base.name).replace('[platform]', state.platform);
        }
      }

      result.setAttribute('data-status', status);
      var titleEl = result.querySelector('[data-sim-compat-title]');
      var bodyEl = result.querySelector('[data-sim-compat-body]');
      var fixEl = result.querySelector('[data-sim-compat-fix]');
      if (titleEl) titleEl.textContent = title || '';
      if (bodyEl) bodyEl.textContent = body || '';
      if (fixEl) {
        if (fix) { fixEl.textContent = fix; fixEl.href = fixUrl || '#'; fixEl.hidden = false; }
        else { fixEl.hidden = true; }
      }
    }

    tool.querySelectorAll('[data-sim-compat-platform]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        state.platform = btn.getAttribute('data-sim-compat-platform');
        tool.querySelectorAll('[data-sim-compat-platform]').forEach(function (b) {
          b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
        });
        render();
      });
    });

    var select = tool.querySelector('[data-sim-compat-base]');
    if (select) select.addEventListener('change', function () { state.base = select.value || null; render(); });

    render();
  }

  /* -------------------------------------------------------------- companions */

  function initBundle(root) {
    root.querySelectorAll('[data-sim-bundle]').forEach(function (bundle) {
      var format = bundle.getAttribute('data-money-format') || '${{amount}}';
      var totalEl = bundle.querySelector('[data-sim-bundle-total]');
      var countEl = bundle.querySelector('[data-sim-bundle-count]');
      var btn = bundle.querySelector('[data-sim-bundle-add]');

      function boxes() {
        return bundle.querySelectorAll('[data-sim-bundle-item] input[type="checkbox"]');
      }

      function recalc() {
        var total = 0, count = 0;
        boxes().forEach(function (cb) {
          if (cb.checked) { total += parseInt(cb.getAttribute('data-price'), 10) || 0; count++; }
        });
        if (totalEl) totalEl.textContent = money(total, format);
        if (countEl) countEl.textContent = count;
        if (btn) btn.toggleAttribute('disabled', count === 0);
      }

      boxes().forEach(function (cb) { cb.addEventListener('change', recalc); });

      if (btn) {
        btn.addEventListener('click', function () {
          var fd = new FormData();
          var i = 0;
          bundle.querySelectorAll('[data-sim-bundle-item] input[type="checkbox"]:checked').forEach(function (cb) {
            fd.append('items[' + i + '][id]', cb.value);
            fd.append('items[' + i + '][quantity]', '1');
            i++;
          });
          if (!i) return;
          var original = btn.textContent;
          btn.setAttribute('disabled', 'disabled');
          addToCart(fd, {})
            .then(function () {
              btn.textContent = btn.getAttribute('data-added-label') || 'Added ✓';
              setTimeout(function () { btn.textContent = original; recalc(); }, 1600);
            })
            .catch(function () {
              btn.textContent = 'Try again';
              btn.removeAttribute('disabled');
            });
        });
      }

      recalc();
    });
  }

  /* -------------------------------------------------------------------- boot */

  function initSection(section) {
    if (section.getAttribute(BOOTED) === 'true') return;
    section.setAttribute(BOOTED, 'true');
    initGallery(section);
    initVariants(section);
    initCart(section);
    initAccordions(section);
    initSticky(section);
    initShipping(section);
    initNotifier(section);
    initCompat(section);
    initBundle(section);
  }

  function boot(scope) {
    var root = scope || document;
    if (root.nodeType === 1 && root.matches && root.matches('[data-sim-section]')) initSection(root);
    root.querySelectorAll('[data-sim-section]').forEach(initSection);
  }

  function teardown(scope) {
    scope.querySelectorAll('[data-sim-section]').forEach(function (s) {
      if (s._simStickyObserver) { s._simStickyObserver.disconnect(); delete s._simStickyObserver; }
      s.removeAttribute(BOOTED);
    });
  }

  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', function () { boot(); });

  // Theme editor: sections are replaced wholesale, so tear down and re-init.
  document.addEventListener('shopify:section:load', function (e) {
    teardown(e.target);
    boot(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    teardown(e.target);
  });
})();